/**
 * ArticleHistoryPanel — the article reader's History (decision 39 / C3,
 * Library LB6-T9, acceptance L6-12).
 *
 * Lists the article's saved versions (GET /wiki/articles/:id/versions — newest
 * first, a page at a time: number, who, when, change summary), opens one
 * READ-ONLY (GET …/versions/:n, its stored HTML drawn by ArticleBody — the
 * component the reader draws the article with, so a version reads exactly as
 * it will once restored; review LB6-T9-REV-01) and,
 * for a person who holds write:wiki — the key the restore route declares
 * (decision 26) — restores it (POST …/versions/:n/restore) after an in-app
 * confirmation, never a native `window.confirm`.
 *
 * A restore first saves the article's current text as a new version (so it can
 * be undone from this same History), then writes the version back and, for a
 * published article, RE-INDEXES it through the Document writer's
 * `restoreVersion` — Ask Argus answers from the restored text. Every Library
 * list then re-asks the server (invalidateLibraryLists), which re-reads the
 * article the reader shows (its text and its version number) and this panel's
 * own list.
 *
 * Two numberings exist and this panel shows only one (review LB6T9-BF-01):
 * "Version N" is a row of the versions list (numbered as saved). The
 * article's own counter (the reader's "Version vN", bumped by every save,
 * tag-only ones included) is not a versions-list number, so the panel never
 * shows it beside them; a restore names the list number the replaced text was
 * saved as. A version whose text IS the article's current text says so, and
 * restoring it says that nothing changed (review LB6T9-BF-03).
 *
 * This file is the ONE place the web reads an article's versions (L6-12
 * mustNotExistAfter: a second versions fetch path outside the panel).
 *
 * Decision 61 (LB6c-T2): a restore never lowers the article's label — the
 * server raises it to the more restrictive of the article's own and the
 * version's. The panel never compares two labels (the no-web-derivation
 * rule): the version detail's `restoreRaisesClassificationTo` and the restore
 * answer's `classificationRaisedTo` are the server's answer, and the panel
 * only says it — naming a label only while the reader's classification policy
 * shows that axis's control (decision 28; the reader's own badge rule). A
 * restore whose re-index failed (502) cannot carry the raise; the reader
 * re-reads the article (invalidateLibraryLists) and shows the new label.
 *
 * A failed read is an error with a Retry, never an empty history (L6-17's
 * rule). Visibility is the server's: the version routes ask THE resolver
 * (policy/document-visibility.ts via wiki.ts `readableArticle`), so an article
 * this person may not see answers 404 here as everywhere.
 */
import { useState } from 'react';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { History, Loader2, RotateCcw, X } from 'lucide-react';
import {
  exportControlLabel,
  sensitivityLabel,
  type ArticleDetailShape,
  type ArticleVersionDetailShape,
  type ArticleVersionPage,
  type ArticleVersionRestoreResult,
  type ClassificationAxis,
  type ClassificationRaise,
} from '@argusai/shared';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { apiClient } from '@/lib/api-client';
import { getApiErrorCode, getApiErrorMessage } from '@/lib/api-error';
import { useHasPermission } from '@/lib/use-has-permission';
import {
  HIDDEN_CLASSIFICATION_CONTROLS,
  useClassificationPolicy,
  type ClassificationControlsView,
} from '@/lib/use-classification-policy';
import { invalidateLibraryLists, wikiArticlesQueryKey } from '@/lib/use-library-documents';
import { cn } from '@/lib/utils';
import { ArticleBody } from '@/components/kb/ArticleBody';

// ─── Query keys ─────────────────────────────────────────────────────────────
//
// Under the wiki articles prefix, so invalidateLibraryLists (which every
// article write already calls) re-asks the history with the article.

/** The versions list of one article (its pages, newest first). */
export const articleVersionsQueryKey = (articleId: string) =>
  [...wikiArticlesQueryKey, 'versions', articleId] as const;

/** One version of one article, with its text. */
export const articleVersionQueryKey = (articleId: string, versionNumber: number) =>
  [...wikiArticlesQueryKey, 'versions', articleId, versionNumber] as const;

const versionsPath = (articleId: string) => `/wiki/articles/${encodeURIComponent(articleId)}/versions`;

/**
 * One page of the versions list, newest first: the first page when `before`
 * is null, else the versions numbered below `before` (the previous page's
 * `nextBefore`). The server bounds the page (review LB6T9-BF-04).
 */
export function fetchArticleVersionPage(articleId: string, before: number | null): Promise<ArticleVersionPage> {
  return apiClient.get<ArticleVersionPage>(
    before === null ? versionsPath(articleId) : `${versionsPath(articleId)}?before=${before}`,
  );
}

function isNotFound(err: unknown): boolean {
  return (err as { status?: number } | null)?.status === 404;
}

function when(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

/** The article's current text, as the reader holds it — what a version is compared with. */
export type ArticleCurrentText = Pick<ArticleDetailShape, 'title' | 'content' | 'excerpt'>;

/**
 * Whether a version holds the article's current text — the same three fields
 * the Document writer's restoreVersion compares before it writes anything.
 */
export function isArticleCurrentText(
  version: Pick<ArticleVersionDetailShape, 'title' | 'content' | 'excerpt'>,
  current: ArticleCurrentText,
): boolean {
  return (
    version.title === current.title &&
    version.content === current.content &&
    (version.excerpt ?? null) === (current.excerpt ?? null)
  );
}

// ─── The restore ────────────────────────────────────────────────────────────

/** How a raised axis is named — one phrase per axis the server can answer (CLASSIFICATION_AXES). */
const RAISED_AXIS_PHRASE: Readonly<Record<ClassificationAxis, (raise: ClassificationRaise) => string>> = Object.freeze({
  sensitivity: (raise: ClassificationRaise) => `sensitivity to ${sensitivityLabel(raise.sensitivity)}`,
  exportControl: (raise: ClassificationRaise) => `export control to ${exportControlLabel(raise.exportControl)}`,
});

/**
 * The words that name a raised classification (decision 61) — ONLY the axes
 * the server says rise (`raisedAxes`, LB6cT2-REV-01), each only while the
 * reader's classification policy shows its control (decision 28, the
 * reader's own badge rule): "sensitivity to Restricted", "export control to
 * ITAR", or both. An axis that keeps the article's own value is never named
 * as a raise, and the result is not called "the version's label" — the
 * server takes the strictest of the two per axis, so it is often a mix.
 * Null when no raised axis shows (or the answer names none), and the caller
 * then says the classification rises without naming it. The pair and the
 * axes are the SERVER's answer; nothing here compares two labels.
 *
 * It renders the server's list as the server sends it: each axis of
 * `raise.raisedAxes`, in the server's order, becomes its phrase — the web
 * does not walk the axes itself and decide which rose (the no-web-derivation
 * rule). The one thing the browser adds is decision 28's display rule, the
 * reader's own classification policy (`controls`, itself the server's
 * answer on GET /knowledge/classification-policy). An axis this build cannot
 * name is skipped, never guessed at.
 */
export function raisedClassificationName(
  raise: ClassificationRaise,
  controls: ClassificationControlsView,
): string | null {
  const axes: readonly ClassificationAxis[] = Array.isArray(raise.raisedAxes) ? raise.raisedAxes : [];
  const parts = axes
    .filter((axis) => controls[axis] === true && Object.prototype.hasOwnProperty.call(RAISED_AXIS_PHRASE, axis))
    .map((axis) => RAISED_AXIS_PHRASE[axis](raise));
  return parts.length > 0 ? parts.join(' and ') : null;
}

/** "The article's classification was raised: sensitivity to X." — or, with no raised axis shown, that it was raised. */
function raisedClassificationSentence(raise: ClassificationRaise, controls: ClassificationControlsView): string {
  const name = raisedClassificationName(raise, controls);
  return name !== null
    ? `The article's classification was raised: ${name}.`
    : "The article's classification was raised.";
}

/**
 * What a restore came to. `restored-not-indexed`: the server COMMITTED the
 * restore but could not re-index it (502 RAG_INGEST_FAILED — the route says
 * so: "Restore the same version again to retry indexing"), so the reader
 * still re-reads the article, and the person is told Ask Argus cannot find it
 * yet. `unchanged`: the article already held that version's text, so
 * nothing was written and no version was saved (review LB6T9-BF-03).
 */
export type RestoreVersionOutcome = 'restored' | 'unchanged' | 'restored-not-indexed' | 'failed';

/**
 * THE restore, once its dialog is confirmed: POST
 * /wiki/articles/:id/versions/:n/restore. Success is shown only after the
 * server answered and the lists were re-asked; a refusal (403, 404, a
 * network error) is an error toast with no refresh.
 */
export async function restoreArticleVersion(
  target: { articleId: string; versionNumber: number },
  refreshLists: () => Promise<unknown>,
  /** Which labels the toast may name (decision 28); hidden names none. */
  controls: ClassificationControlsView = HIDDEN_CLASSIFICATION_CONTROLS,
): Promise<RestoreVersionOutcome> {
  let result: ArticleVersionRestoreResult;
  try {
    result = await apiClient.post<ArticleVersionRestoreResult>(
      `${versionsPath(target.articleId)}/${target.versionNumber}/restore`,
      {},
    );
  } catch (err: unknown) {
    if (getApiErrorCode(err) === 'RAG_INGEST_FAILED') {
      await refreshLists();
      toast.error(getApiErrorMessage(err, 'The version was restored, but search indexing failed.'));
      return 'restored-not-indexed';
    }
    toast.error(getApiErrorMessage(err, 'Restore failed'));
    return 'failed';
  }
  await refreshLists();
  // Decision 61: the server's answer says whether the label rose.
  const raised = result.classificationRaisedTo
    ? ` ${raisedClassificationSentence(result.classificationRaisedTo, controls)}`
    : '';
  if (!result.changed) {
    toast.info(
      result.classificationRaisedTo
        ? `Version ${result.restoredFromVersion} is already the article's text — the text did not change, and no version was saved.${raised}`
        : `Version ${result.restoredFromVersion} is already the article's text — nothing changed, and no version was saved.`,
    );
    return 'unchanged';
  }
  // The number the replaced text was saved as is a versions-list number —
  // the one the person finds in this list (review LB6T9-BF-01).
  toast.success(
    result.savedAsVersion !== null
      ? `Restored version ${result.restoredFromVersion}. The text it replaced is saved as version ${result.savedAsVersion}.${raised}`
      : `Restored version ${result.restoredFromVersion}.${raised}`,
  );
  return 'restored';
}

/** "Restore version N?" — the in-app confirmation (never `window.confirm`). */
export function RestoreVersionConfirmDialog({
  versionNumber, isCurrentText = false, raisesTo = null, controls = HIDDEN_CLASSIFICATION_CONTROLS,
  open, busy, onCancel, onConfirm,
}: {
  versionNumber: number;
  /** The version already holds the article's current text: restoring it writes nothing. */
  isCurrentText?: boolean;
  /** The server's answer: the labels restoring this version raises the article to (decision 61); null when none rise. */
  raisesTo?: ClassificationRaise | null;
  /** Which labels may be named (decision 28); hidden names none. */
  controls?: ClassificationControlsView;
  open: boolean;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <AlertDialog open={open} onOpenChange={(next) => { if (!next && !busy) onCancel(); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Restore version {versionNumber}?</AlertDialogTitle>
          <AlertDialogDescription>
            {isCurrentText && raisesTo ? (
              // LB6cT2-REV-02: the label below still rises, so "changes nothing" would be false.
              <>
                This version is already the article&rsquo;s current text, so the text does not change and no version is
                saved. A published article is re-indexed.
              </>
            ) : isCurrentText ? (
              <>
                This version is already the article&rsquo;s current text, so restoring it changes nothing and saves no
                version. A published article is re-indexed.
              </>
            ) : (
              <>
                The article&rsquo;s current text is saved as a new version first, so this can be undone from History.
                A published article is re-indexed, and Ask Argus answers from the restored text.
              </>
            )}
          </AlertDialogDescription>
          {raisesTo ? <RestoreRaisesClassificationNote raisesTo={raisesTo} controls={controls} /> : null}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            data-testid="confirm-restore-version"
            disabled={busy}
            onClick={(e) => {
              // Keep the dialog open until the request answers.
              e.preventDefault();
              onConfirm();
            }}
          >
            {busy ? <Loader2 className="h-3 w-3 animate-spin mr-1.5" /> : null}
            Restore
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** Decision 61: the restore raises the article's classification — said in the confirm. */
function RestoreRaisesClassificationNote({
  raisesTo, controls,
}: {
  raisesTo: ClassificationRaise;
  controls: ClassificationControlsView;
}) {
  const name = raisedClassificationName(raisesTo, controls);
  return (
    <p className="text-sm text-muted-foreground" data-testid="restore-raises-classification">
      {name !== null ? (
        <>Restoring it raises the article&rsquo;s classification: <strong>{name}</strong>.</>
      ) : (
        <>Restoring it raises the article&rsquo;s classification.</>
      )}{' '}
      This is an audited classification change, and people not cleared for it will no longer see the article.
    </p>
  );
}

/**
 * Restore — drawn only for a person who holds write:wiki, the key the restore
 * route declares (LIBRARY_CONTROLS.restoreArticleVersion); for anyone else it
 * is not rendered at all (L6-04). Exported for the per-role control test.
 */
export function ArticleVersionRestoreButton({
  articleId, versionNumber, isCurrentText = false, raisesTo = null, controls = HIDDEN_CLASSIFICATION_CONTROLS, onRestored,
}: {
  articleId: string;
  versionNumber: number;
  isCurrentText?: boolean;
  /** The version detail's `restoreRaisesClassificationTo` (decision 61). */
  raisesTo?: ClassificationRaise | null;
  /** The reader's classification controls (decision 28). */
  controls?: ClassificationControlsView;
  onRestored: () => void;
}) {
  const canRestore = useHasPermission('write', 'wiki');
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!canRestore) return null;

  const runRestore = async () => {
    setBusy(true);
    try {
      const outcome = await restoreArticleVersion(
        { articleId, versionNumber },
        () => invalidateLibraryLists(queryClient),
        controls,
      );
      if (outcome !== 'failed') {
        setConfirming(false);
        onRestored();
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        aria-label={`Restore version ${versionNumber}`}
        title="Make this version the article's current text"
        onClick={() => setConfirming(true)}
      >
        <RotateCcw className="h-3.5 w-3.5 mr-1" />Restore
      </Button>
      <RestoreVersionConfirmDialog
        versionNumber={versionNumber}
        isCurrentText={isCurrentText}
        raisesTo={raisesTo}
        controls={controls}
        open={confirming}
        busy={busy}
        onCancel={() => setConfirming(false)}
        onConfirm={() => void runRestore()}
      />
    </>
  );
}

// ─── One version, read-only ─────────────────────────────────────────────────

/** One saved version: its title, who saved it and when, and its text — read-only. */
export function ArticleVersionView({
  articleId, versionNumber, current, onRestored,
}: {
  articleId: string;
  versionNumber: number;
  /** The article's current text, from the reader (no second read of the article). */
  current: ArticleCurrentText;
  onRestored: () => void;
}) {
  // Which labels the confirm and the toast may name — the reader's own rule (decision 28).
  const { controls } = useClassificationPolicy();
  const versionQ = useQuery<ArticleVersionDetailShape, Error>({
    queryKey: articleVersionQueryKey(articleId, versionNumber),
    queryFn: () => apiClient.get<ArticleVersionDetailShape>(`${versionsPath(articleId)}/${versionNumber}`),
    retry: false,
    staleTime: 0,
  });

  if (versionQ.isLoading) {
    return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }
  if (versionQ.isError || !versionQ.data) {
    return (
      <div role="alert" data-testid="article-version-read-error" className="text-center text-destructive py-12 space-y-2 text-sm">
        <p>
          {isNotFound(versionQ.error)
            ? `Version ${versionNumber} no longer exists.`
            : `Version ${versionNumber} could not be loaded: ${getApiErrorMessage(versionQ.error, 'the request failed')}`}
        </p>
        {!isNotFound(versionQ.error) ? (
          <Button variant="outline" size="sm" onClick={() => void versionQ.refetch()}>Retry</Button>
        ) : null}
      </div>
    );
  }
  const version = versionQ.data;
  const isCurrent = isArticleCurrentText(version, current);

  return (
    <div className="space-y-3" data-testid="article-version-view">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold truncate">{version.title}</h3>
            <Badge variant="secondary" className="shrink-0">Version {version.versionNumber} · read-only</Badge>
            {isCurrent ? (
              <Badge variant="outline" className="shrink-0" data-testid="article-version-is-current">
                The article&rsquo;s current text
              </Badge>
            ) : null}
          </div>
          <p className="text-xs text-muted-foreground">
            Saved by {version.editedByName ?? 'an unknown person'} · {when(version.editedAt)}
            {version.editSummary ? ` · ${version.editSummary}` : ''}
          </p>
        </div>
        <ArticleVersionRestoreButton
          articleId={articleId}
          versionNumber={version.versionNumber}
          isCurrentText={isCurrent}
          raisesTo={version.restoreRaisesClassificationTo}
          controls={controls}
          onRestored={onRestored}
        />
      </div>
      <div className="rounded-md border bg-muted/30 p-4 max-h-[60vh] overflow-y-auto">
        <ArticleBody html={version.content} emptyText="This version has no text." />
      </div>
    </div>
  );
}

// ─── The versions list ──────────────────────────────────────────────────────

/**
 * The saved versions, newest first, a page at a time (review LB6T9-BF-04):
 * older ones load on request. One is selected to be read.
 */
export function ArticleVersionList({
  articleId, selected, onSelect,
}: {
  articleId: string;
  selected: number | null;
  onSelect: (versionNumber: number) => void;
}) {
  const versionsQ = useInfiniteQuery({
    queryKey: articleVersionsQueryKey(articleId),
    queryFn: ({ pageParam }: { pageParam: number | null }) => fetchArticleVersionPage(articleId, pageParam),
    initialPageParam: null as number | null,
    getNextPageParam: (last: ArticleVersionPage) => last.nextBefore ?? undefined,
    retry: false,
    staleTime: 0,
  });

  if (versionsQ.isLoading) {
    return <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;
  }
  // A failed read is an error, never an empty history. Only a failed OLDER
  // page keeps the rows already read (with the error below them, and Load
  // older versions to try again).
  const olderPageFailed = versionsQ.isFetchNextPageError;
  if ((versionsQ.isError && !olderPageFailed) || !versionsQ.data) {
    return (
      <div role="alert" data-testid="article-history-read-error" className="text-center text-destructive py-8 space-y-2 text-sm">
        <p>The history could not be loaded: {getApiErrorMessage(versionsQ.error, 'the request failed')}</p>
        <Button variant="outline" size="sm" onClick={() => void versionsQ.refetch()}>Retry</Button>
      </div>
    );
  }
  const versions = versionsQ.data.pages.flatMap((page) => page.data);
  if (versions.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-8 text-center" data-testid="article-history-empty">
        No earlier versions yet. A version is saved each time the article&rsquo;s text changes.
      </p>
    );
  }

  return (
    <ul className="space-y-1" aria-label="Saved versions">
      {versions.map((v) => (
        <li key={v.id}>
          <button
            type="button"
            data-version={v.versionNumber}
            aria-pressed={selected === v.versionNumber}
            onClick={() => onSelect(v.versionNumber)}
            className={cn(
              'w-full text-left rounded-md border px-3 py-2 text-sm transition-colors hover:bg-muted/50',
              selected === v.versionNumber && 'border-primary bg-muted',
            )}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium">Version {v.versionNumber}</span>
              <span className="text-xs text-muted-foreground">{when(v.editedAt)}</span>
            </div>
            <div className="text-xs text-muted-foreground truncate">{v.title}</div>
            <div className="text-xs text-muted-foreground truncate">
              {v.editedByName ?? 'An unknown person'}
              {v.editSummary ? ` · ${v.editSummary}` : ''}
            </div>
          </button>
        </li>
      ))}
      {versionsQ.hasNextPage ? (
        <li>
          <Button
            variant="ghost"
            size="sm"
            className="w-full"
            data-testid="article-history-load-older"
            disabled={versionsQ.isFetchingNextPage}
            onClick={() => void versionsQ.fetchNextPage()}
          >
            {versionsQ.isFetchingNextPage ? <Loader2 className="h-3 w-3 animate-spin mr-1.5" /> : null}
            Load older versions
          </Button>
        </li>
      ) : null}
      {olderPageFailed ? (
        <li role="alert" className="text-xs text-destructive text-center" data-testid="article-history-older-error">
          Older versions could not be loaded: {getApiErrorMessage(versionsQ.error, 'the request failed')}
        </li>
      ) : null}
    </ul>
  );
}

// ─── The panel ──────────────────────────────────────────────────────────────

/** The History panel the reader opens: the versions on the left, the one selected on the right. */
export function ArticleHistoryPanel({
  articleId, current, onClose,
}: {
  articleId: string;
  current: ArticleCurrentText;
  onClose: () => void;
}) {
  const [selected, setSelected] = useState<number | null>(null);

  return (
    <Card data-testid="article-history-panel">
      <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-sm flex items-center gap-1">
          <History className="h-3.5 w-3.5" />History
        </CardTitle>
        <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Close history" title="Close history" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-4">
          <div className="max-h-[60vh] overflow-y-auto">
            <ArticleVersionList articleId={articleId} selected={selected} onSelect={setSelected} />
          </div>
          <div className="min-w-0">
            {selected === null ? (
              <p className="text-sm text-muted-foreground py-8 text-center">Select a version to read it.</p>
            ) : (
              <ArticleVersionView
                articleId={articleId}
                versionNumber={selected}
                current={current}
                onRestored={() => setSelected(null)}
              />
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
